import { useEffect, useState } from "react";
import { getProfile } from "../lib/api";
import { summarizeBoardPlacements } from "../lib/find-placements";
import type { UserProfile } from "../lib/types";
import { BoardCard } from "../components/BoardCard";
import { ProductImage, sourceLabelFor } from "../components/ProductImage";
import { navigate } from "../lib/router";

export function ProfileRoute() {
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProfile().then((data) => {
      if (!cancelled) setProfile(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!profile) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <h1 className="font-bold tracking-[-0.04em] text-[clamp(1.7rem,4vw,2.8rem)] mb-6">
        {profile.username}
      </h1>
      <section className="mb-10">
        <h2 className="font-bold tracking-[-0.04em] text-[clamp(1.15rem,2.5vw,1.6rem)] mb-4">
          Finds
        </h2>
        {profile.finds.length === 0 ? (
          <p className="text-muted">No finds yet.</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
            {profile.finds.map((find) => {
              const placementSummary = summarizeBoardPlacements(find.boardPlacements);

              return (
                <article
                  key={find.id}
                  className="panel-surface rounded-[14px] overflow-hidden cursor-pointer"
                  onClick={() => navigate(`/finds/${find.id}`)}
                >
                  <ProductImage imageUrl={find.imageUrl} title={find.title} imageScale={find.imageScale} imageOffsetX={find.imageOffsetX} imageOffsetY={find.imageOffsetY} />
                  <div className="p-3">
                    <p className="font-medium text-sm leading-tight line-clamp-2">
                      {find.title}
                    </p>
                    <p className="text-muted text-xs mt-1">
                      {sourceLabelFor(find.url)}
                    </p>
                    {placementSummary && (
                      <p className="text-xs mt-1 text-muted">
                        {placementSummary}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-bold tracking-[-0.04em] text-[clamp(1.15rem,2.5vw,1.6rem)] mb-4">
          Boards
        </h2>
        {profile.boards.length === 0 ? (
          <p className="text-muted">No boards yet.</p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
            {profile.boards.map((board) => (
              <BoardCard key={board.id} board={board} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
